import React from 'react';
import { Eye, EyeOff } from 'lucide-react';

// Extracted from SettingsPage (2026-07-28) so the credential form can be rendered both by
// the legacy per-service Online PG tabs and by the named-profile editor under Routing —
// two places editing the same shape, and a field that exists in one but not the other is
// exactly the kind of drift that ends with a profile silently missing its salt.
export const MASKED = '••••••••';

// The shape a brand-new gateway profile starts from. Lives here beside the form that edits
// it so a field can't be added to one without the other — three call sites now (the profile
// editor, the amount-split editor, and whatever comes next).
export const blankGatewayProfile = () => ({
  name: '', provider: 'ablepay', testMode: false, isEnabled: true,
  keyId: '', keySecret: '', webhookSecret: '',
  ablepayApiKey: '', ablepaySalt: '', ablepayPgApiUrl: '', ablepayShowConvenienceFee: true,
  passFeeToCustomer: false,
  feesByMethod: {
    upi: { percent: 0, flat: 0 }, debitCard: { percent: 0, flat: 0 },
    creditCard: { percent: 0, flat: 0 }, netbanking: { percent: 0, flat: 0 },
  },
});

export const SECTION = ({ title, children }) => (
  <div style={{ marginBottom: 28 }}>
    <div style={{
      fontSize: '.75rem', fontWeight: 700, color: 'var(--text-muted)',
      textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 14,
      paddingBottom: 6, borderBottom: '1px solid var(--border)'
    }}>{title}</div>
    {children}
  </div>
);

export const FIELD = ({ label, hint, required, children }) => (
  <div className="form-group">
    <label className="form-label">{label}{required && <span className="required"> *</span>}</label>
    {children}
    {hint && <div className="form-hint">{hint}</div>}
  </div>
);

// A password input that treats MASKED as "unchanged": focusing clears the bullets so a new
// value can be typed, blurring an untouched-and-now-empty box puts them back. The server
// side of that contract is in routes/settings.js — MASKED arriving in a PUT is dropped
// rather than written, so a save that doesn't touch this field can't wipe the credential.
const SecretInput = ({ value, onChange, show, onToggleShow }) => (
  <div style={{ position: 'relative', maxWidth: 400 }}>
    <input
      className="form-control font-mono"
      type={show ? 'text' : 'password'}
      value={value || ''}
      onFocus={() => { if (value === MASKED) onChange(''); }}
      onBlur={() => { if (value === '') onChange(MASKED); }}
      onChange={e => onChange(e.target.value)}
      style={{ paddingRight: 40 }}
    />
    <button type="button" onClick={onToggleShow} style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 0 }}>
      {show ? <EyeOff size={15} /> : <Eye size={15} />}
    </button>
  </div>
);

const FEE_METHODS = [
  { key: 'upi', label: 'UPI' },
  { key: 'debitCard', label: 'Debit Card' },
  { key: 'creditCard', label: 'Credit Card' },
  { key: 'netbanking', label: 'Net Banking' },
];

// pg: the gateway config being edited; setField(key, val); ui/setUiField carry the
// show/hide state for the three secret inputs; setFeeMethod(method, field, val).
export default function GatewayCredentialFields({ pg, ui, setField, setUiField, setFeeMethod }) {
  return (
    <>
      <SECTION title="Online PG — Provider">
        <FIELD label="Provider">
          <select className="form-control" style={{ maxWidth: 280 }}
            value={pg.provider} onChange={e => setField('provider', e.target.value)}>
            <option value="razorpay">Razorpay</option>
            <option value="ablepay">AblePay</option>
            <option value="cashfree">Cashfree</option>
            <option value="payu">PayU</option>
            <option value="other">Other</option>
          </select>
        </FIELD>
        <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', fontSize: '.88rem', marginTop: 4 }}>
          <input type="checkbox" checked={!!pg.testMode} onChange={e => setField('testMode', e.target.checked)} />
          Test / Sandbox mode
        </label>
        {pg.provider === 'ablepay' && pg.testMode && (
          // AblePay rejects mode=TEST outright unless sandbox access is explicitly enabled
          // on the merchant account (error 1090), and the failure surfaces to the customer
          // as a raw JSON blob on a blank page rather than anything diagnosable.
          <div style={{ fontSize: '.78rem', color: 'var(--warning, #b45309)', marginTop: 8 }}>
            AblePay rejects test mode unless sandbox access is enabled on your merchant account —
            leave this off unless AblePay has confirmed it for this API key.
          </div>
        )}
      </SECTION>

      {pg.provider === 'ablepay' ? (
        <>
          <SECTION title="Online PG — AblePay Credentials">
            <FIELD label="API Key" required hint="From AblePay's merchant dashboard (pgmrm.ablepay.co.in)">
              <input className="form-control font-mono" value={pg.ablepayApiKey || ''}
                onChange={e => setField('ablepayApiKey', e.target.value.trim())}
                placeholder="e.g. 9b62ff8e-f03b-1587-afds-b630edad99df" style={{ maxWidth: 400 }} />
            </FIELD>
            <FIELD label="Salt" required hint={pg.ablepaySalt === MASKED ? 'Leave unchanged to keep current secret' : 'Used to sign every request/response — never shared publicly'}>
              <SecretInput value={pg.ablepaySalt} onChange={v => setField('ablepaySalt', v)}
                show={ui.showAblepaySalt} onToggleShow={() => setUiField('showAblepaySalt', !ui.showAblepaySalt)} />
            </FIELD>
            <FIELD label="Gateway URL" required hint="The {pg_api_url} domain from AblePay's onboarding — just the domain, no https:// or path">
              <input className="form-control font-mono" value={pg.ablepayPgApiUrl || ''}
                onChange={e => setField('ablepayPgApiUrl', e.target.value.trim())}
                placeholder="e.g. pgapi.ablepay.co.in" style={{ maxWidth: 400 }} />
            </FIELD>
          </SECTION>

          <SECTION title="Online PG — AblePay Display">
            <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', fontSize: '.88rem' }}>
              <input type="checkbox" checked={!!pg.ablepayShowConvenienceFee}
                onChange={e => setField('ablepayShowConvenienceFee', e.target.checked)} />
              Show the convenience fee breakdown to the customer on AblePay's payment page
            </label>
          </SECTION>
        </>
      ) : (
        <SECTION title="Online PG — API Credentials">
          <FIELD label="Key ID" required hint="Public API key / merchant ID from the gateway dashboard">
            <input className="form-control font-mono" value={pg.keyId || ''}
              onChange={e => setField('keyId', e.target.value.trim())}
              placeholder="e.g. rzp_test_xxxxxxxxxxxx" style={{ maxWidth: 400 }} />
          </FIELD>
          <FIELD label="Key Secret" required hint={pg.keySecret === MASKED ? 'Leave unchanged to keep current secret' : ''}>
            <SecretInput value={pg.keySecret} onChange={v => setField('keySecret', v)}
              show={ui.showSecret} onToggleShow={() => setUiField('showSecret', !ui.showSecret)} />
          </FIELD>
          <FIELD label="Webhook Secret" hint={pg.webhookSecret === MASKED ? 'Leave unchanged to keep current secret' : 'Used to verify payment webhook signatures'}>
            <SecretInput value={pg.webhookSecret} onChange={v => setField('webhookSecret', v)}
              show={ui.showWebhookSecret} onToggleShow={() => setUiField('showWebhookSecret', !ui.showWebhookSecret)} />
          </FIELD>
        </SECTION>
      )}

      <SECTION title="Online PG — Customer Surcharge">
        <p style={{ fontSize: '.8rem', color: 'var(--text-muted)', marginTop: -4, marginBottom: 12 }}>
          When checked, the customer pays the plan price plus a gateway fee — you always net the full
          plan amount either way. Leave unchecked and you absorb the gateway's cut yourself.
        </p>
        <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', fontSize: '.9rem', marginBottom: 10, fontWeight: 600 }}>
          <input type="checkbox" checked={!!pg.passFeeToCustomer}
            onChange={e => setField('passFeeToCustomer', e.target.checked)} />
          Charge the gateway fee to the customer (I don't bear any loss)
        </label>
        {pg.passFeeToCustomer && (
          <>
            <p style={{ fontSize: '.78rem', color: 'var(--text-muted)', marginBottom: 10 }}>
              Enter each payment method's actual rate — since the customer only picks their method on the
              gateway's own page (after we've already sent the request), the <strong>highest</strong> rate
              entered below is the one actually applied, so you're covered no matter which they choose.
            </p>
            {FEE_METHODS.map(({ key, label }) => (
              <div key={key} style={{ display: 'flex', gap: 14, alignItems: 'flex-end', marginBottom: 10 }}>
                <FIELD label={`${label} — Fee %`}>
                  <input type="number" step="0.01" min="0" max="100" className="form-control" style={{ maxWidth: 130 }}
                    value={pg.feesByMethod?.[key]?.percent ?? 0}
                    onChange={e => setFeeMethod(key, 'percent', Number(e.target.value))} />
                </FIELD>
                <FIELD label={`${label} — Flat Fee (₹)`}>
                  <input type="number" step="0.01" min="0" className="form-control" style={{ maxWidth: 130 }}
                    value={pg.feesByMethod?.[key]?.flat ?? 0}
                    onChange={e => setFeeMethod(key, 'flat', Number(e.target.value))} />
                </FIELD>
              </div>
            ))}
          </>
        )}
      </SECTION>

      <SECTION title="Online PG — Status">
        <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', fontSize: '.88rem' }}>
          <input type="checkbox" checked={!!pg.isEnabled} onChange={e => setField('isEnabled', e.target.checked)} />
          Enable online payment collection via this gateway
        </label>
      </SECTION>
    </>
  );
}
